import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Users, TrendingUp, Package } from 'lucide-react';
import { toast } from 'sonner';

const MODULE_COLORS = {
  courses: 'bg-violet-500',
  memberships: 'bg-blue-500',
  donations: 'bg-rose-500',
  ppob: 'bg-amber-500',
  affiliates: 'bg-emerald-500',
  high_ticket: 'bg-indigo-500',
};

export default function AdminModuleAdoption() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/module-adoption');
      setData(res.data);
    } catch { toast.error('Gagal memuat laporan'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, []);

  if (loading) return <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return null;

  const maxCount = Math.max(...(data.module_stats || []).map(m => m.tenant_count), 1);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Module Adoption Report</h1>
        <p className="text-sm text-slate-500 mt-1">Laporan penggunaan modul oleh tenant aktif</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#2D6CDF]/10 flex items-center justify-center">
              <Users size={22} className="text-[#2D6CDF]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{data.total_active_tenants}</p>
              <p className="text-xs text-slate-500">Tenant Aktif</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Package size={22} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{(data.module_stats || []).length}</p>
              <p className="text-xs text-slate-500">Modul Tersedia</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center">
              <TrendingUp size={22} className="text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {data.module_stats?.length > 0
                  ? `${Math.round(data.module_stats.reduce((sum, m) => sum + m.adoption_rate, 0) / data.module_stats.length)}%`
                  : '0%'}
              </p>
              <p className="text-xs text-slate-500">Rata-rata Adopsi</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Module Stats Bar Chart */}
      <Card className="border-slate-200 shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 size={18} className="text-[#2D6CDF]" />
            Adopsi Per Modul
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(data.module_stats || []).map(m => (
              <div key={m.module}>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-sm font-medium text-slate-700">{m.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">{m.tenant_count} tenant</span>
                    <Badge className={`${MODULE_COLORS[m.module] || 'bg-slate-500'} text-white text-xs min-w-[48px] justify-center`}>
                      {m.adoption_rate}%
                    </Badge>
                  </div>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${MODULE_COLORS[m.module] || 'bg-slate-500'}`}
                    style={{ width: `${maxCount > 0 ? (m.tenant_count / maxCount) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tenant Breakdown */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Detail Penggunaan Tenant</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">#</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tenant</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Modul Aktif</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Daftar Modul</th>
                </tr>
              </thead>
              <tbody>
                {(data.tenant_breakdown || []).map((t, idx) => (
                  <tr key={t.tenant_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-slate-400 text-xs">{idx + 1}</td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-900">{t.name || '-'}</p>
                      <p className="text-xs text-slate-500">{t.subdomain}.tokomoe.com</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[...Array(6)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-3 h-3 rounded-sm ${i < t.module_count ? 'bg-[#2D6CDF]' : 'bg-slate-100'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{t.module_count}/6</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(t.modules_used || []).map(m => (
                          <Badge key={m} variant="outline" className="text-xs px-1.5 py-0">{m}</Badge>
                        ))}
                        {t.module_count === 0 && <span className="text-slate-400 text-xs">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!data.tenant_breakdown?.length && (
                  <tr><td colSpan={4} className="py-12 text-center text-slate-400">Belum ada data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
